/**
 * Leaderboards - Web version (Steam stubbed out)
 *
 * Phase 5: Will be replaced with Supabase leaderboards
 * For now, submission functions no-op and read functions
 * still try the Edge Function (which works without Steam SDK).
 */

import { debug } from '../debug.js';

export const LEADERBOARD_NAMES = {
  NET_WORTH: 'Net Worth',
  RARE_AIRCRAFT: 'Rare Aircraft',
  TOTAL_FLIGHTS: 'Total Flights',
  TOTAL_PASSENGERS: 'Total Passengers',
  MYTHIC_ROLLERS: 'Mythic Rollers',
  OPERATIONAL_GAINS: 'Operational Acquired Gains',
};

const MYTHIC_HOLDER_VALIDITY_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Steam is never available on web
 */
export function isSteamAvailable() {
  return false;
}

export const isSteamLeaderboardAvailable = isSteamAvailable;

export async function getSteamId() {
  return null;
}

/**
 * Score submission — no-op on web (no Steam SDK)
 * Phase 5: will submit to Supabase leaderboards
 */
export async function submitScore(steamId, leaderboard, score) {
  debug(`[Leaderboards] Web version — score submission skipped (${leaderboard}: ${score})`);
  return false;
}

export const submitNetWorth = (steamId, amount) => submitScore(steamId, 'net_worth', Math.floor(amount));
export const submitTotalFlights = (steamId, count) => submitScore(steamId, 'total_flights', count);
export const submitTotalPassengers = (steamId, count) => submitScore(steamId, 'total_passengers', count);
export const submitRareAircraft = (steamId, count) => submitScore(steamId, 'rare_aircraft', count);
export const submitMythicRollers = (steamId, count) => submitScore(steamId, 'mythic_rollers', count);
export const submitOperationalGains = (steamId, amount) => submitScore(steamId, 'operational_gains', Math.floor(amount));

export async function submitBatchScores(steamId, scores) {
  return {};
}

/**
 * Calculate leaderboard scores from world state
 * Still useful — will be used by Supabase leaderboards in Phase 5
 */
export function calculateLeaderboardScores(worldState) {
  const cash = worldState?.cash ?? 0;

  const fleet = worldState?.fleet ?? [];
  const fleetValue = fleet.reduce((sum, aircraft) => {
    return sum + (aircraft.marketValue ?? aircraft.purchasePrice ?? 0);
  }, 0);

  let hubValue = 0;
  if (worldState?.gameMode === 'simple') {
    const hubs = worldState?.hubs ?? [];
    hubValue = hubs.reduce((sum, hub) => sum + (hub.purchasePrice ?? 0), 0);
  } else {
    const slots = worldState?.slots ?? [];
    hubValue = slots.length * 1000000;
  }

  const netWorth = cash + fleetValue + hubValue;

  const eos = worldState?.eos ?? {};
  const collection = eos.collection ?? {};
  const rareAircraft = Object.values(collection).filter(Boolean).length;

  const stats = worldState?.stats ?? {};
  const totalFlights = stats.completedFlights ?? 0;
  const totalPassengers = stats.passengersMoved ?? 0;
  const operationalGains = worldState?.dailyProfit ?? 0;

  return {
    netWorth: Math.floor(netWorth / 100),
    rareAircraft,
    totalFlights,
    totalPassengers,
    operationalGains: Math.floor(operationalGains / 100),
  };
}

export async function syncSteamLeaderboards(worldState) {
  debug('[Leaderboards] Web version — sync skipped');
  return { success: false, error: 'Steam not available on web' };
}

export async function uploadLeaderboardScore(leaderboardKey, score) {
  return { success: false, error: 'Steam not available on web' };
}

/**
 * Get leaderboard entries via Supabase Edge Function
 * This still works on web — it reads from Steam Web API server-side
 */
export async function getLeaderboardEntries(leaderboardName, type = 'Global', start = 0, end = 99) {
  try {
    debug(`[Leaderboards] Fetching ${leaderboardName} via Edge Function...`);

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseKey) {
      return { success: false, error: 'Supabase not configured', entries: [] };
    }

    const response = await fetch(`${supabaseUrl}/functions/v1/steam_get_leaderboard`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseKey}`
      },
      body: JSON.stringify({
        leaderboardName,
        rangeStart: start,
        rangeEnd: end
      })
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      console.error(`[Leaderboards] Fetch error for ${leaderboardName}:`, data.error);
      return { success: false, error: data.error || 'Failed to fetch', entries: [] };
    }

    debug(`[Leaderboards] Got ${data.entries?.length ?? 0} entries for ${leaderboardName}`);
    return { success: true, entries: data.entries ?? [] };
  } catch (error) {
    console.error(`[Leaderboards] Fetch error for ${leaderboardName}:`, error);
    return { success: false, error: error.message || 'Unknown error', entries: [] };
  }
}

export async function getUserLeaderboardRank(leaderboardName) {
  return { success: false, error: 'Not implemented for web', rank: null, score: null };
}

export async function getAllUserRanks() {
  return { success: false, error: 'Steam not available on web' };
}

export async function uploadMythicRoll() {
  return { success: false, error: 'Steam not available on web' };
}

export async function getCurrentMythicHolder() {
  try {
    const result = await getLeaderboardEntries(
      LEADERBOARD_NAMES.MYTHIC_ROLLERS,
      'Global',
      0,
      0
    );

    if (!result.success || !result.entries || result.entries.length === 0) {
      return { holder: null, isValid: false };
    }

    const topEntry = result.entries[0];
    const rollTimestamp = topEntry.score;
    const now = Date.now();
    const isWithin30Days = (now - rollTimestamp) < MYTHIC_HOLDER_VALIDITY_MS;

    if (!isWithin30Days) {
      return {
        holder: {
          steamName: topEntry.steamName || topEntry.personaName || 'Unknown',
          rolledAt: rollTimestamp,
        },
        isValid: false,
        expired: true,
      };
    }

    return {
      holder: {
        steamName: topEntry.steamName || topEntry.personaName || 'Unknown',
        rolledAt: rollTimestamp,
      },
      isValid: true,
    };
  } catch (error) {
    console.error('[Leaderboards] Error fetching mythic holder:', error);
    return { holder: null, isValid: false, error: error.message || 'Unknown error' };
  }
}

export default {
  LEADERBOARD_NAMES,
  isSteamAvailable,
  getSteamId,
  submitScore,
  calculateLeaderboardScores,
  syncSteamLeaderboards,
  uploadLeaderboardScore,
  getLeaderboardEntries,
  getUserLeaderboardRank,
  getAllUserRanks,
  uploadMythicRoll,
  getCurrentMythicHolder,
  submitNetWorth,
  submitTotalFlights,
  submitTotalPassengers,
  submitRareAircraft,
  submitMythicRollers,
  submitOperationalGains,
  submitBatchScores,
};
